import { CardItem, ModalOpenButton, StatusChip } from "@msys/ui";
import { Link as LinkIcon } from "@mui/icons-material";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { ConnectIntegrationModal } from "./ConnectIntegrationModal.js";
import type { IntegrationMeta } from "../integrationRegistry.js";
import { integrationSettingsPath } from "../paths.js";

// Tinted chip colors per health state, mirroring the health-dot palette.
const healthChipColors: Record<string, { bg: string; text: string }> = {
  ok: { bg: "#e8f5e9", text: "#2e7d32" },
  degraded: { bg: "#fff3e0", text: "#e65100" },
  unreachable: { bg: "#ffebee", text: "#c62828" },
};
const fallbackHealthChip = { bg: "#fff3e0", text: "#e65100" };

interface HealthData {
  health: string;
  details?: any;
  updatedAt?: string | null;
}

export interface IntegrationCardData {
  integrationId: string;
  name: string;
  isConnected: boolean;
  isForeignAccountIdRequired: boolean;
  health: HealthData | null;
  meta: IntegrationMeta;
}

interface Props {
  data: IntegrationCardData;
}

/**
 * Overview card (design review 2026-07-14): not expandable — a connected
 * card is clickable as a whole and leads to the integration's settings
 * page. Unconnected cards keep the Connect button on the right, which
 * always opens the connect modal; integrations flagged `connectViaPage`
 * continue from the modal to the create-mode settings page.
 */
export const IntegrationCard = ({ data }: Props) => {
  const { t } = useTranslate("Integrations");
  const navigate = useNavigate();

  const {
    integrationId,
    name,
    isConnected,
    isForeignAccountIdRequired,
    health,
    meta,
  } = data;
  const hasHealth = health !== null;

  const relativeTime = React.useMemo(() => {
    if (!health?.updatedAt) return null;
    const diff = Date.now() - new Date(health.updatedAt).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return t("just now");
    if (minutes < 60)
      return t("{minutes} min ago", { minutes, ns: "Integrations" });
    const hours = Math.floor(minutes / 60);
    return t("{hours} h ago", { hours, ns: "Integrations" });
  }, [health?.updatedAt, t]);

  const healthChip = health
    ? (healthChipColors[health.health] ?? fallbackHealthChip)
    : fallbackHealthChip;
  const healthLabel = !health
    ? ""
    : health.health === "ok"
      ? t("Ok")
      : health.health === "degraded"
        ? t("Degraded")
        : health.health === "unreachable"
          ? t("Unreachable")
          : health.health;

  const connectButton = (
    <Button size="small" variant="outlined" startIcon={<LinkIcon />}>
      {t("Connect")}
    </Button>
  );

  const goToSettings = () => navigate(integrationSettingsPath(integrationId));

  return (
    <CardItem
      hoverable={isConnected}
      onClick={isConnected ? goToSettings : undefined}
      // Connected cards navigate as a whole — expose them to keyboard and
      // assistive tech as links
      {...(isConnected
        ? {
            role: "link",
            tabIndex: 0,
            "aria-label": `${name} — ${t("Settings")}`,
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === "Enter") {
                e.preventDefault();
                goToSettings();
              }
            },
          }
        : {})}
      sx={theme => ({
        // Left accent is always the integration's assigned colour — the health
        // state is still surfaced via the status chip in the card body.
        borderLeft: `3px solid ${meta.color}`,
        ...(isConnected
          ? {
              "&:focus-visible": {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 1,
              },
            }
          : {}),
      })}
    >
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: "center", p: 1.5 }}
      >
        {/* Logo box */}
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            backgroundColor: meta.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: "#fff", fontWeight: 700, fontSize: 11 }}
          >
            {meta.shortName}
          </Typography>
        </Box>

        {/* Name + description */}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" noWrap>
            {name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {t(meta.description as any)}
          </Typography>
        </Box>

        {/* Category chip */}
        {meta.category && (
          <StatusChip
            label={t(meta.category as any)}
            size="small"
            variant="outlined"
          />
        )}

        {/* Health or status — health state + last-seen time as chips */}
        {isConnected && health && (
          <>
            <StatusChip
              label={healthLabel}
              size="small"
              backgroundColor={healthChip.bg}
              textColor={healthChip.text}
            />
            {relativeTime && (
              // Plain Chip (not StatusChip) so the relative time keeps its
              // normal capitalization — StatusChip force-uppercases its label.
              <Chip
                label={relativeTime}
                size="small"
                sx={{
                  height: "auto",
                  backgroundColor: "#f5f5f5",
                  ".MuiChip-label": { color: "#616161", fontWeight: 500 },
                }}
              />
            )}
          </>
        )}
        {isConnected && !hasHealth && meta.Dashboard && (
          <StatusChip
            label={t("Initializing...")}
            size="small"
            backgroundColor="#fff3e0"
            textColor="#e65100"
          />
        )}
        {isConnected && !hasHealth && !meta.Dashboard && (
          <StatusChip
            label={t("Connected")}
            size="small"
            backgroundColor="#e8f5e9"
            textColor="#2e7d32"
          />
        )}

        {/* Connect button for unconnected — always opens the modal */}
        {!isConnected && (
          <ModalOpenButton
            Modal={ConnectIntegrationModal}
            modalProps={{
              integrationId,
              integrationName: name,
              isForeignAccountIdRequired,
            }}
          >
            {connectButton}
          </ModalOpenButton>
        )}
      </Stack>
    </CardItem>
  );
};
